import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  select,
  axisBottom,
  scaleLinear,
  scaleBand,
  axisLeft,
  selectAll,
} from "d3";
import { toTitleCase } from "./helpers";
import HelperText from "../../../../components/atoms/HelperText";
import { whiteColor } from "../../../../common/helpers/colors";

const useResizeObserver = (ref) => {
  // doing this for resizing
  const [dimensions, setDimensions] = useState(null);
  useEffect(() => {
    const observeTarget = ref.current;
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setDimensions(entry.contentRect);
      });
    });
    resizeObserver.observe(observeTarget);
    return () => {
      resizeObserver.unobserve(observeTarget);
    };
  }, [ref]);
  return dimensions;
};
function ProteinAtlasPathogenicityHistogram({ data }) {
  const divRef = useRef();
  const svgRef = useRef();
  const dimensions = useResizeObserver(divRef);
  let currentWidth;
  if (dimensions) {
    currentWidth = Math.max(dimensions.width, 800);
  }

  // will be called initially and on every data or dimensions change
  useEffect(() => {
    if (!currentWidth) {
      return;
    }
    selectAll(`#divContainer > svg > *`).remove();

    const svg = select(svgRef.current);
    const margin = { top: 20, right: 20, bottom: 120, left: 80 };
    const width = currentWidth - margin.left - margin.right;
    const height = svg.attr("height") - margin.top - margin.bottom;
    const x = scaleBand().rangeRound([0, width]).padding(0.3).domain(data);
    const y = scaleLinear().rangeRound([height, 0]).domain([0, 100]).nice();

    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const xAxis = axisBottom(x).tickFormat((d) => toTitleCase(d.cancer_name));

    g.append("g")
      .attr("class", "axis axis--x protein")
      .attr("transform", `translate(0,${height})`)
      .call(xAxis)
      .selectAll("text")
      .attr("class", "varsome-axis-label")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", ".15em")
      .attr("transform", "rotate(-45)")
      .attr("font-size", "1.2em");

    g.append("g")
      .attr("class", "varsome-axis axis--y")
      .call(axisLeft(y).ticks(10))
      .append("text")
      .attr("class", "varsome-axis-label")
      .attr("y", 6)
      .attr("dy", "0.71em")
      .attr("text-anchor", "end")
      .text("value");

    g.append("text")
      .attr("text-anchor", "middle") // this makes it easy to centre the text as the transform is applied to the anchor
      .attr("transform", `translate(-40,${height / 2})rotate(-90)`) // text is drawn off the screen top left, move down and out and rotate
      .attr("font", "helvetica")
      .text("Patients %");

    g.append("text")
      .attr("text-anchor", "middle") // this makes it easy to centre the text as the transform is applied to the anchor
      .attr("transform", `translate(${width / 2},${height + 110})`) // centre below axis
      .attr("font", "helvetica")
      .text("Cancer Type");

    // For each value in our dataset, places and styles a bar on the chart.
    const rect = g.selectAll("rect").data(data).enter().append("g");

    /* Create tooltip */
    const tooltip = select("body")
      .append("div")
      .attr("class", "d3-tooltip")
      .style("position", "absolute")
      .style("z-index", "10")
      .style("visibility", "hidden")
      .style("padding", "10px")
      .style("background", "rgba(0,0,0,0.8)")
      .style("border-radius", "4px")
      .style("color", whiteColor);

    const mouseover = (event, d) => {
      tooltip
        .html(
          `<h6><b>${toTitleCase(d.cancer_name)}</b></h6>
          <div>${d.totalHighMedium} of ${
            d.total
          } show high/medium expression</div>
          <div>High: ${d.high_risk}</div>
          <div>Medium: ${d.medium_risk}</div>
          <div>Low: ${d.low_risk}</div>
          <div>Not Detected: ${d.not_detected}</div>`
        )
        .style("visibility", "visible");
    };

    const mousemove = (event) => {
      tooltip
        .style("left", `${event.pageX + 10}px`)
        .style("top", `${event.pageY}px`);
    };

    const mouseleave = () => {
      tooltip.html(``).style("visibility", "hidden");
    };

    rect
      .append("rect")
      .attr("class", "varsome-rpkm-bar")
      .attr("x", (d) => x(d))
      .attr("y", (d) => y(d.value))
      .attr("width", x.bandwidth())
      .attr("height", (d) => height - y(d.value))
      .on("mouseover", mouseover)
      .on("mousemove", mousemove)
      .on("mouseleave", mouseleave);

    rect
      .append("text")
      .attr("text-anchor", "middle")
      .attr("y", (d) => y(d.value) - 5)
      .attr("x", (d) => x(d) + x.bandwidth() / 2)
      .style("fill", "black")
      .attr("font", "helvetica")
      .attr("font-size", "0.8em")
      .text((d) => `${parseFloat(d.value.toFixed(1))}%`);

    selectAll(".axis.axis--x.protein>.tick")
      .on("mouseover", mouseover)
      .on("mousemove", mousemove)
      .on("mouseleave", mouseleave);
  }, [data, dimensions]);

  return (
    <React.Fragment>
      <div className="tw-text-base tw-mt-8 tw-mb-2 tw-ml-4 ">
        Protein Expression Summary
        <HelperText
          title=""
          customText="The percentage of cancer patients with high and medium protein expression level"
        />
      </div>
      <div
        className="tw-w-full tw-overflow-x-auto"
        ref={divRef}
        id="divContainer"
      >
        <svg ref={svgRef} width={currentWidth} height="400" />
      </div>
    </React.Fragment>
  );
}

ProteinAtlasPathogenicityHistogram.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      high_risk: PropTypes.string,
      medium_risk: PropTypes.string,
      low_risk: PropTypes.string,
      not_detected: PropTypes.string,
      total: PropTypes.number,
      totalHighMedium: PropTypes.number,
      value: PropTypes.number,
    })
  ),
};

ProteinAtlasPathogenicityHistogram.defaultProps = {
  data: null,
};
export default ProteinAtlasPathogenicityHistogram;
